import React, { FC } from 'react';
import { Marker, Tooltip } from 'react-leaflet';
import { withCoLocationOffset, coLocationTooltipOffset } from './shared/coLocationOffset';
import { CONNECTION_KIND_LABELS, connectionKindOf } from '../../../types/network';
import { AnyNetworkElement, Hausanschluss, SmartMeterGateway, Steuerbox, TechnischeRessource } from '../../../types/network';
import L, { LeafletMouseEvent, LatLngTuple } from 'leaflet';
import { HighlightHalo } from '../markers/HighlightHalo';
import { markerZIndexOffset } from './markerZOrder';
import { shortElementName } from '../../../utils/elementDisplayName';
import { getLoadColorStyle } from './shared/MarkerIcons';
import { useSettingsStore } from '../../../store/settingsStore';
import { useHistoricalPlaybackStore } from '../../../store/historicalPlaybackStore';
import { fmt } from '../../../utils/numberFormat';
import {
  CONNECTION_KIND_SVG, OTHER_DEVICE_SVG, RESOURCE_ORDER, RESOURCE_SHORT, RESOURCE_SVG, RESOURCE_TONE,
  RESOURCE_TYPE_LABEL, SMGW_SVG, STEUERBOX_SVG, symbolSvg as svg,
} from './shared/houseSymbols';

/** Get aggregated SMGW total power in kW, or undefined if no realtime data */
function getSmgwTotalPowerKw(smgws?: SmartMeterGateway[]): number | undefined {
  if (!smgws || smgws.length === 0) return undefined;
  const active = smgws.filter(s => s.hasRealtimeData && s.currentPowerW != null);
  if (active.length === 0) return undefined;
  let totalW = 0;
  for (const s of active) totalW += s.currentPowerW ?? 0;
  return totalW / 1000;
}

/** Escape data strings (street names come from GIS imports) for divIcon HTML. */
function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

type MapDevice = { name: string; short: string; detail: string; path: string; tone: string; facts: string[]; zoomPower?: string };

const STEUERPFAD_TEXT = { relay: 'Relais (analog)', eebus: 'EEBUS', ems: 'Energiemanager' } as const;

function resourceControlFacts(resource: TechnischeRessource, boxes: Steuerbox[]): string[] {
  if (resource.controlKind === 'none') return ['Steuerung: keine'];
  const facts: string[] = [];
  const box = boxes.find(item => item.id === resource.steuerboxId);
  if (resource.controlKind === 'ems') facts.push('Steuerung: Energiemanager');
  else if (resource.controlKind === 'direct') facts.push('Steuerung: direkt');
  if (box?.steuerpfad && box.steuerpfad !== 'unknown') {
    facts.push(`Ansteuerung: ${STEUERPFAD_TEXT[box.steuerpfad]}`);
  } else if (box?.type === 'analog') {
    facts.push('Ansteuerung: Relais (analog)');
  }
  if (resource.relayNumber != null && box?.type === 'analog') facts.push(`Relais: R${resource.relayNumber}`);
  return facts;
}

interface IconOptions {
  iconSize: [number, number];
  iconAnchor: [number, number];
}

/**
 * The house's connection power (kW) is derived at import from its main fuse
 * (P = √3 · 400 V · I · cos φ 0,9, GeoJsonProcessorService). Invert it for the
 * tooltip when it lands on a standard fuse rating — the default 44 kVA of
 * houses without fuse data yields no standard rating and gets no hint.
 */
const STANDARD_FUSES_A = [16, 20, 25, 32, 35, 40, 50, 63, 80, 100, 125, 160, 200, 250, 315, 400];
const fuseHint = (connectionPowerKw: number): string => {
  const amps = (connectionPowerKw * 1000) / (Math.sqrt(3) * 400 * 0.9);
  const std = STANDARD_FUSES_A.find(a => Math.abs(a - amps) / a < 0.03);
  return std ? ` (Sicherung ${std} A)` : '';
};

interface NetworkMarkerProps {
  element: AnyNetworkElement;
  isSelected: boolean;
  isHighlighted: boolean;
  onClick?: (e: LeafletMouseEvent) => void;
  offsetIndex?: number;
  simulatedLoadPercentage?: number;
  powerMeasurement?: {
    totalActivePower: number;
    timestamp: number;
    simValue: number | null;
    resultValue: number | null;
    loadingPercent?: number | null;
    phases: {
      L1: { voltage: number; current: number; power: number };
      L2: { voltage: number; current: number; power: number };
      L3: { voltage: number; current: number; power: number };
    };
  };
  connectionPower?: number;
}

// Create a custom equality function to prevent unnecessary re-renders
const arePropsEqual = (prevProps: NetworkMarkerProps, nextProps: NetworkMarkerProps) => {
  return (
    prevProps.element === nextProps.element &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.isHighlighted === nextProps.isHighlighted &&
    prevProps.offsetIndex === nextProps.offsetIndex &&
    prevProps.simulatedLoadPercentage === nextProps.simulatedLoadPercentage &&
    prevProps.powerMeasurement?.totalActivePower === nextProps.powerMeasurement?.totalActivePower &&
    prevProps.powerMeasurement?.loadingPercent === nextProps.powerMeasurement?.loadingPercent &&
    prevProps.connectionPower === nextProps.connectionPower
  );
};

export const NetworkMarker: FC<NetworkMarkerProps> = React.memo(({
  element,
  isSelected,
  isHighlighted,
  onClick,
  offsetIndex,
  simulatedLoadPercentage,
  powerMeasurement,
  connectionPower
}) => {
  const houseLoadColoring = useSettingsStore(s => s.settings?.mapSettings?.houseLoadColoring ?? true);
  const trafoThresholds = useSettingsStore(s => s.thresholds.transformerLoadLevels);
  // In historical playback the map must not blend in LIVE SMGW power — the
  // archived powerflow frame is the single data source for the shown instant.
  const playbackMode = useHistoricalPlaybackStore(s => s.mode);
  const isHistorical = playbackMode === 'historical';
  // Live SMGW values belong to the live view only, never on an archive frame or a calculated Prüffall
  const showLiveSmgw = playbackMode === 'live';
  // Every marker of an overlap group sits on the TRUE coordinate; the icons of index ≥ 1 are
  // shifted by a constant number of pixels (shared/coLocationOffset), so the pile looks the
  // same on every zoom level and marker 1 always shows where the elements really are.
  const position = React.useMemo(
    (): LatLngTuple => [element.coordinates.lat, element.coordinates.lng],
    [element]
  );

  // Cache icon options by type to avoid recalculating
  const getIconOptions = React.useCallback((type: string): IconOptions => {
    switch (type) {
      case 'muffe':
        return {
          iconSize: [12, 12],
          iconAnchor: [6, 6]
        };
      case 'hausanschluss':
        return {
          iconSize: [28, 28],
          iconAnchor: [14, 14]
        };
      case 'kabelverteilschrank':
        return {
          iconSize: [28, 28],
          iconAnchor: [14, 14]
        };
      default:
        return {
          iconSize: [32, 32],
          iconAnchor: [16, 16]
        };
    }
  }, []);

  const icon = React.useMemo(() => {
    const iconOptions = getIconOptions(element.type);

    if (element.type === 'hausanschluss') {
      const hausanschluss = element as Hausanschluss;
      const hasSMGW = hausanschluss.smgws?.length > 0;
      const hasSteuerbox = hausanschluss.steuerboxes?.length > 0;
      const hasSimulatedLoad = simulatedLoadPercentage && simulatedLoadPercentage > 0;

      // Create HTML parts separately to improve readability and performance
      const htmlParts = [];

      // Load-based coloring: prefer SMGW real measurement, fall back to power flow
      // (historical playback: archived powerflow only, never live SMGW)
      const smgwPowerKw = showLiveSmgw ? getSmgwTotalPowerKw(hausanschluss.smgws) : undefined;
      const livePowerKw = smgwPowerKw ?? powerMeasurement?.totalActivePower;
      const powerSource = smgwPowerKw !== undefined ? 'SMGW · gemessen'
        : isHistorical ? 'Lastfluss · Archiv' : playbackMode === 'check' ? 'Lastfluss · Prüffall' : 'Lastfluss · berechnet';
      const isExporting = livePowerKw !== undefined && livePowerKw < 0;
      const loadBgStyle = houseLoadColoring ? getLoadColorStyle(livePowerKw, isExporting) : '';
      const bgClass = loadBgStyle ? '' : 'bg-element-connection/80';

      // Main container - wrapped in zoom-scaling div
      const borderClass = isSelected || isHighlighted
        ? 'border-yellow-400 dark:border-yellow-300'
        : houseLoadColoring && isExporting
          ? 'border-green-500 dark:border-green-400'
          : 'border-gray-800 dark:border-primary-900';
      const expandClass = isSelected ? 'marker-expanded' : '';
      htmlParts.push(`<div class="marker-expand-wrapper ${expandClass}" style="transform: scale(var(--marker-scale, 1)); transform-origin: center;">`);
      htmlParts.push(`<div class="w-7 h-7 ${bgClass} rounded-full border-2 ${borderClass} flex items-center justify-center relative shadow-md" style="${loadBgStyle}">`);

      // The central symbol identifies the connection; equipment is disclosed
      // separately as the map gains space at higher zoom levels.
      const kind = connectionKindOf(hausanschluss);
      htmlParts.push(`<span class="ha-symbol" title="${CONNECTION_KIND_LABELS[kind]}">${svg(CONNECTION_KIND_SVG[kind])}</span></div></div>`);
      if (livePowerKw !== undefined && Number.isFinite(livePowerKw)) {
        // A power figure always names its origin (ERP_MELO_TAF_MEASUREMENT.md → Frontend-Anzeige)
        const powerLabel = smgwPowerKw !== undefined ? 'SMGW' : isHistorical ? 'Archiv' : playbackMode === 'check' ? 'Prüffall' : 'Lastfluss';
        htmlParts.push(`<div class="ha-power-chip" aria-label="${escapeHtml(fmt(livePowerKw, 1))} kW, ${powerSource}"><span>${powerLabel}</span><strong>${escapeHtml(fmt(livePowerKw, 1))} kW</strong></div>`);
      }

      const devices: MapDevice[] = [];
      if (hasSMGW) devices.push({
        name: 'Smart Meter Gateway', short: 'SMGW', detail: String(hausanschluss.smgws.length),
        tone: 'meter', facts: [
          `Anzahl: ${hausanschluss.smgws.length}`,
          ...(hausanschluss.smgws.some(gateway => gateway.hasRealtimeData === true) ? ['Echtzeitdaten: vorhanden'] : []),
        ],
        path: SMGW_SVG
      });
      if (hasSteuerbox) devices.push({
        name: 'Steuerbox', short: 'StB', detail: String(hausanschluss.steuerboxes.length),
        tone: 'control', facts: [
          `Anzahl: ${hausanschluss.steuerboxes.length}`,
          ...[...new Set(hausanschluss.steuerboxes.map(box => box.type === 'analog' ? 'Typ: FNN-Steuerbox (analog)' : box.type === 'digital' ? 'Typ: Energiemanager' : 'Typ: FNN-Steuerbox (hybrid)'))],
          ...[...new Set(hausanschluss.steuerboxes.flatMap(box => box.steuerpfad && box.steuerpfad !== 'unknown' ? [`Ansteuerung: ${STEUERPFAD_TEXT[box.steuerpfad]}`] : []))],
        ],
        path: STEUERBOX_SVG
      });
      const resourceRank = (type: string) => {
        const index = RESOURCE_ORDER.indexOf(type);
        return index < 0 ? RESOURCE_ORDER.length : index;
      };
      const groups = new Map<string, { count: number; power: number; controllableCount: number; controllablePower: number; controlFacts: Set<string> }>();
      for (const resource of hausanschluss.technischeRessourcen ?? []) {
        const group = groups.get(resource.type) ?? { count: 0, power: 0, controllableCount: 0, controllablePower: 0, controlFacts: new Set<string>() };
        group.count += 1;
        if (Number.isFinite(resource.maxPower) && resource.maxPower > 0) group.power += resource.maxPower;
        if (resource.controlKind === 'direct' || resource.controlKind === 'ems') {
          group.controllableCount += 1;
          if (Number.isFinite(resource.maxPower) && resource.maxPower > 0) group.controllablePower += resource.maxPower;
        }
        for (const fact of resourceControlFacts(resource, hausanschluss.steuerboxes ?? [])) group.controlFacts.add(fact);
        groups.set(resource.type, group);
      }
      for (const [type, group] of [...groups].sort(([left], [right]) =>
        resourceRank(left) - resourceRank(right)
      )) {
        const label = RESOURCE_TYPE_LABEL[type] ?? type;
        const unit = type === 'pv_anlage' ? 'kWp' : 'kW';
        const detail = `${group.count}×${group.power > 0 ? ` · max. ${fmt(group.power, 1)} ${unit}` : ''}`;
        devices.push({
          name: label, short: RESOURCE_SHORT[type] ?? 'Gerät', detail,
          tone: RESOURCE_TONE[type] ?? 'other',
          zoomPower: group.controllablePower > 0 ? `max. ${fmt(group.controllablePower, 1)} ${unit}` : undefined,
          facts: [`Anzahl: ${group.count}`, ...(group.power > 0 ? [`Maximalleistung${group.count > 1 ? ' gesamt' : ''}: ${fmt(group.power, 1)} ${unit}`] : []), ...(group.controllableCount > 0 && group.controllableCount < group.count ? [`Davon steuerbar: ${group.controllableCount}`] : []), ...group.controlFacts],
          path: RESOURCE_SVG[type] ?? OTHER_DEVICE_SVG
        });
      }
      if (devices.length) {
        htmlParts.push(`<div class="ha-device-dock" aria-label="Ausstattung: ${escapeHtml(devices.map(device => device.name).join(', '))}">${devices.map(device =>
          `<span class="ha-device-badge ha-device-badge--${device.tone}" tabindex="0" aria-label="${escapeHtml(`${device.name}. ${device.facts.join('. ')}`)}">${svg(device.path)}<span class="ha-device-short">${escapeHtml(device.short)}</span>${device.zoomPower ? `<span class="ha-device-power">${escapeHtml(device.zoomPower)}</span>` : ''}<span class="ha-device-tooltip" role="tooltip"><strong>${escapeHtml(device.name)}</strong>${device.facts.map(fact => `<span>${escapeHtml(fact)}</span>`).join('')}</span></span>`
        ).join('')}</div>`);
      }
      const equipment = devices.map(device =>
        `<div class="ha-device">${svg(device.path)}<span>${escapeHtml(device.name)}</span><span class="ha-device-detail">${escapeHtml(device.detail)}</span></div>`
      );
      const reading = livePowerKw !== undefined
        ? `<strong>${escapeHtml(fmt(livePowerKw, 1))} <span>kW</span></strong><span>${powerSource}</span>`
        : '<span>Kein Leistungswert vorhanden</span>';
      htmlParts.push(`<div class="ha-label" style="--device-clearance:${devices.length * 22}px">
        <div class="ha-identity"><span class="ha-kind">${escapeHtml(CONNECTION_KIND_LABELS[kind])}</span><span class="ha-address">${escapeHtml(shortElementName(element) ?? '')}</span></div>
        <div class="ha-equipment">${equipment.length ? equipment.join('') : '<span class="ha-empty">Keine Geräte hinterlegt</span>'}</div>
        <div class="ha-reading">${reading}${hasSimulatedLoad ? `<span>Simulation: ${escapeHtml(String(simulatedLoadPercentage))} %</span>` : ''}</div>
      </div>`);

      return L.divIcon({
        className: 'custom-div-icon',
        html: htmlParts.join(''),
        ...iconOptions
      });
    }

    // Create a more efficient icon generation system with cached templates
    const getIconHtml = (type: string): string => {
      switch (type) {
        case 'trafostation': {
          // Loading badge (live: measured station loading first, else the load flow; playback: the archived frame)
          const trafoLoading = powerMeasurement?.loadingPercent;
          let loadingBadge = '';
          if (trafoLoading != null) {
            const badgeColor = trafoLoading >= trafoThresholds.red
              ? 'bg-red-500 border-red-700'
              : trafoLoading >= trafoThresholds.orange
                ? 'bg-orange-400 border-orange-600'
                : 'bg-green-500 border-green-700';
            loadingBadge = `<div class="absolute -bottom-2 left-1/2 -translate-x-1/2 px-1 min-w-[26px] h-4 ${badgeColor} rounded-sm border flex items-center justify-center shadow-sm">
              <span class="text-[9px] font-bold text-white leading-none">${Math.round(trafoLoading)}%</span>
            </div>`;
          }
          return `<div class="w-8 h-8 bg-element-trafo/60 dark:bg-element-trafo/70 rounded-lg border-2 ${isSelected || isHighlighted ? 'border-yellow-400 dark:border-yellow-300' : 'border-gray-800 dark:border-primary-900'} flex items-center justify-center relative shadow-md">
            <span class="absolute inset-0 flex items-center justify-center text-[18px] font-bold text-gray-800 dark:text-amber-100 translate-y-[-2px]">⚡</span>
            ${loadingBadge}
          </div>`;
        }
        case 'kabelverteilschrank':
          return `<div class="w-7 h-7 bg-element-cabinet/70 dark:bg-element-cabinet/80 rounded-sm border-2 ${isSelected || isHighlighted ? 'border-yellow-400 dark:border-yellow-300' : 'border-gray-800 dark:border-primary-900'} flex items-center justify-center relative shadow-md">
            <span class="absolute inset-0 flex items-center justify-center text-[18px] font-bold text-white dark:text-blue-50 -translate-y-px">□</span>
          </div>`;
        case 'muffe':
          return `<div class="w-4 h-4 bg-element-splice/60 dark:bg-element-splice/70 rounded-full border-2 ${isSelected || isHighlighted ? 'border-yellow-400 dark:border-yellow-300' : 'border-gray-800 dark:border-primary-900'} relative shadow-sm">
            ${offsetIndex !== undefined ? `<span class="absolute -top-2 -right-2 text-[10px] bg-white dark:bg-primary-700 rounded-full w-4 h-4 flex items-center justify-center border border-gray-800 dark:border-primary-900 text-gray-800 dark:text-primary-100 shadow-sm">${offsetIndex + 1}</span>` : ''}
          </div>`;
        default:
          return '';
      }
    };

    return L.divIcon({
      className: 'custom-div-icon',
      // Zoom scaling applies to every element type; the co-location offset
      // stays outermost so it is not multiplied by the scale.
      html: withCoLocationOffset(
        `<div class="marker-expand-wrapper" style="transform: scale(var(--marker-scale, 1)); transform-origin: center;">${getIconHtml(element.type)}</div>`,
        offsetIndex
      ),
      ...iconOptions
    });
  }, [element, isSelected, isHighlighted, offsetIndex, simulatedLoadPercentage, powerMeasurement, getIconOptions, houseLoadColoring, isHistorical, showLiveSmgw, playbackMode, trafoThresholds]);

  if (element.type === 'abgangsleitung') return null;

  return (
    <>
      {isHighlighted && (
        <HighlightHalo position={L.latLng(position[0], position[1])} />
      )}
      {/* No offset indicator needed - we don't show tooltips for overlapping elements */}
      <Marker
        position={position}
        icon={icon}
        zIndexOffset={markerZIndexOffset(element.type, isSelected)}
        draggable={false}
        eventHandlers={{
          click: (e: LeafletMouseEvent) => {
            L.DomEvent.stopPropagation(e);
            e.originalEvent.preventDefault();
            e.originalEvent.stopPropagation();
            if ((e.originalEvent.target as HTMLElement)?.closest?.('.ha-device-badge')) return;
            if (onClick) onClick(e);
          },
          mousedown: (e: LeafletMouseEvent) => L.DomEvent.stopPropagation(e)
        }}
      >
        <Tooltip
          direction="top"
          offset={coLocationTooltipOffset([0, -16], offsetIndex)}
          className={`${element.type === 'hausanschluss' ? 'house-marker-tooltip ' : ''}bg-white/95 dark:bg-primary-800/95 px-1.5 py-0.5 rounded shadow-lg border border-gray-300 dark:border-primary-600`}
          permanent={false}
          sticky={false}
          interactive={false}
        >
          <div className="text-xs flex flex-col gap-0.5">
            <span className="text-gray-900 dark:text-primary-50">{shortElementName(element) ?? element.name}</span>
            {element.type === 'hausanschluss' && (() => {
              const ha = element as Hausanschluss;
              const smgwKw = showLiveSmgw ? getSmgwTotalPowerKw(ha.smgws) : undefined;
              const displayPower = smgwKw ?? powerMeasurement?.totalActivePower;
              const source = smgwKw !== undefined ? 'SMGW' : powerMeasurement ? (isHistorical ? 'Powerflow, Archiv' : 'Powerflow') : undefined;
              // The badges around the icon spelled out, so the map reads
              // without the legend.
              const equipment: string[] = [];
              if (ha.smgws?.length > 0) equipment.push('Smart Meter Gateway');
              if (ha.steuerboxes?.length > 0) equipment.push('Steuerbox');
              ha.technischeRessourcen?.forEach(tr => equipment.push(RESOURCE_TYPE_LABEL[tr.type] ?? tr.type));
              return (
                <>
                  <span className="text-gray-600 dark:text-primary-300">
                    {isHistorical ? 'Historische Last' : 'Aktuelle Last'}: {displayPower !== undefined ? `${fmt(displayPower, 1)} kW` :
                      simulatedLoadPercentage !== undefined ? `${simulatedLoadPercentage}%` : 'N/A'}
                    {source ? ` (${source})` : ''}
                  </span>
                  <span className="text-gray-600 dark:text-primary-300">
                    Anschlussleistung: {connectionPower !== undefined ? `${fmt(connectionPower, 1)} kW${fuseHint(connectionPower)}` : 'N/A'}
                  </span>
                  {connectionKindOf(element as Hausanschluss) !== 'household' && (
                    <span className="text-gray-600 dark:text-primary-300">
                      Anschlussart: {CONNECTION_KIND_LABELS[connectionKindOf(element as Hausanschluss)]}
                    </span>
                  )}
                  {equipment.length > 0 && (
                    <span className="text-gray-600 dark:text-primary-300">
                      Ausstattung: {equipment.join(', ')}
                    </span>
                  )}
                </>
              );
            })()}
            {element.type === 'trafostation' && powerMeasurement?.loadingPercent != null && (
              <span className="text-gray-600 dark:text-primary-300">
                Auslastung{isHistorical ? ' (historisch)' : ''}: {fmt(powerMeasurement.loadingPercent, 1)} %
              </span>
            )}
          </div>
        </Tooltip>
      </Marker>
    </>
  );
}, arePropsEqual);
